import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
// html2canvas loaded dynamically on capture to reduce bundle size
import { Button } from './ui/button';
import { Download, Loader2, Share2, RectangleVertical, Square, X } from 'lucide-react';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { toast } from 'sonner';
import { useNativeShare } from '../hooks/useNativeShare';
import { getDisplayName } from '../lib/displayName';
import client from '../api/client';

const FONT = "'Barlow Condensed', sans-serif";

const RATIOS = {
  story:  { w: 1080, h: 1920, label: '9:16', previewW: 200, previewH: 356, cssAspect: '9/16' },
  square: { w: 1080, h: 1080, label: '1:1',  previewW: 240, previewH: 240, cssAspect: '1/1' },
};

const BG_OPTIONS = [
  { id: 'dark',   labelKey: 'storyGenerator.bgDark',   bg: '#1a1a1a', text: '#ffffff' },
  { id: 'black',  labelKey: 'storyGenerator.bgBlack',  bg: '#000000', text: '#ffffff' },
  { id: 'orange', labelKey: 'storyGenerator.bgOrange', bg: '#FF5C00', text: '#ffffff' },
  { id: 'navy',   labelKey: 'storyGenerator.bgNavy',   bg: '#0f172a', text: '#ffffff' },
  { id: 'white',  labelKey: 'storyGenerator.bgWhite',  bg: '#ffffff', text: '#1a1a1a' },
];

const MEAL_META = {
  breakfast: { labelKey: 'storyGenerator.mealBreakfast', icon: '🌅' },
  lunch:     { labelKey: 'storyGenerator.mealLunch',     icon: '☀️' },
  dinner:    { labelKey: 'storyGenerator.mealDinner',    icon: '🌙' },
  snack:     { labelKey: 'storyGenerator.mealSnack',     icon: '🍎' },
  snacks:    { labelKey: 'storyGenerator.mealSnacks',    icon: '🍎' },
};

function SlideContent({ theme, totals, targets, mealsWithFood, dateLabel, userName, previewW, previewH, t }) {
  const calDiff = totals.calories - (targets.calories || 2000);
  const isOver = calDiff >= 200;
  const isUnder = calDiff <= -200;
  const statusLabel = isOver ? t('storyGenerator.statusOver') : isUnder ? t('storyGenerator.statusUnder') : t('storyGenerator.statusOnTrack');
  const statusColor = isOver ? '#ef4444' : isUnder ? '#f59e0b' : '#22c55e';

  const R = previewW * 0.16;
  const STROKE = previewW * 0.045;
  const circumference = 2 * Math.PI * R;
  const calPct = Math.min((totals.calories / (targets.calories || 2000)), 1.2);
  const fillPct = Math.min(calPct, 1);
  const dashOffset = circumference * (1 - fillPct);
  const sz = R * 2 + STROKE * 2;
  const pad = previewW * 0.05;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', padding: pad, fontFamily: FONT, boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pad * 0.6 }}>
        <div style={{ fontWeight: 900, fontSize: previewW * 0.075, letterSpacing: 1 }}>
          <span style={{ color: '#FF5C00' }}>trained</span>
          <span style={{ color: theme.text }}>byRS</span>
        </div>
        <div style={{ fontSize: previewW * 0.038, color: `${theme.text}80`, fontWeight: 600, textAlign: 'right', maxWidth: '55%' }}>{dateLabel}</div>
      </div>

      {/* Calorie ring + macros row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: pad * 0.8, marginBottom: pad * 0.7 }}>
        {/* Ring */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <svg width={sz} height={sz}>
            <circle cx={R + STROKE} cy={R + STROKE} r={R} fill="none" stroke={`${theme.text}15`} strokeWidth={STROKE} />
            <circle
              cx={R + STROKE} cy={R + STROKE} r={R} fill="none"
              stroke={isOver ? '#ef4444' : '#FF5C00'} strokeWidth={STROKE}
              strokeDasharray={circumference} strokeDashoffset={dashOffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${R + STROKE} ${R + STROKE})`}
            />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: previewW * 0.075, fontWeight: 900, lineHeight: 1, color: theme.text }}>{Math.round(totals.calories)}</div>
            <div style={{ fontSize: previewW * 0.028, color: `${theme.text}60`, lineHeight: 1.2 }}>/ {Math.round(targets.calories || 2000)}</div>
            <div style={{ fontSize: previewW * 0.028, color: `${theme.text}60`, lineHeight: 1.2 }}>kcal</div>
          </div>
        </div>
        {/* Status + macros */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: pad * 0.25 }}>
          <div style={{ fontSize: previewW * 0.04, fontWeight: 800, color: statusColor, letterSpacing: 1 }}>{statusLabel}</div>
          {[
            { label: 'Protein', value: Math.round(totals.protein), color: '#ef4444' },
            { label: 'Carbs',   value: Math.round(totals.carbs),   color: '#f59e0b' },
            { label: 'Fat',     value: Math.round(totals.fat),     color: '#3b82f6' },
          ].map(m => (
            <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: pad * 0.3 }}>
              <div style={{ width: previewW * 0.015, height: previewW * 0.015, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
              <div style={{ fontSize: previewW * 0.038, color: `${theme.text}80`, flex: 1 }}>{m.label}</div>
              <div style={{ fontSize: previewW * 0.042, fontWeight: 800, color: m.color }}>{m.value}g</div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: `${theme.text}25`, marginBottom: pad * 0.6 }} />

      {/* Meals */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: pad * 0.5, overflow: 'hidden' }}>
        {mealsWithFood.map(m => (
          <div key={m.meal_type}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: pad * 0.3 }}>
                <span style={{ fontSize: previewW * 0.048 }}>{m.icon}</span>
                <span style={{ fontSize: previewW * 0.048, fontWeight: 700, color: theme.text }}>{m.label}</span>
              </div>
              <span style={{ fontSize: previewW * 0.042, fontWeight: 700, color: '#FF5C00' }}>{Math.round(m.calories)} kcal</span>
            </div>
            {m.foods.slice(0, 3).map((f, i) => (
              <div key={i} style={{ fontSize: previewW * 0.032, color: `${theme.text}55`, paddingLeft: previewW * 0.065, lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {f.food_name}
              </div>
            ))}
            {m.foods.length > 3 && (
              <div style={{ fontSize: previewW * 0.028, color: `${theme.text}35`, paddingLeft: previewW * 0.065 }}>+{m.foods.length - 3} more</div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: pad * 0.5, borderTop: `1px solid ${theme.text}20` }}>
        <div style={{ fontSize: previewW * 0.042, fontWeight: 900, color: '#FF5C00' }}>trainedbyRS</div>
        <div style={{ fontSize: previewW * 0.034, color: `${theme.text}50` }}>{userName}</div>
      </div>
    </div>
  );
}

export default function DietDayStoryGenerator({ foodLog, targets, date, user, onBehalfOf, onClose }) {
  const { t } = useTranslation();
  const { shareImage: nativeShareImage, downloadImage } = useNativeShare();
  const [ratio, setRatio] = useState('story');
  const [bg, setBg] = useState('dark');
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const slideRef = useRef(null);

  const r = RATIOS[ratio];
  const theme = BG_OPTIONS.find(o => o.id === bg) || BG_OPTIONS[0];

  const entries = foodLog?.entries || [];
  const totals = {
    calories: entries.reduce((s, e) => s + (e.calories || 0), 0),
    protein:  entries.reduce((s, e) => s + (e.protein || 0), 0),
    carbs:    entries.reduce((s, e) => s + (e.carbs || 0), 0),
    fat:      entries.reduce((s, e) => s + (e.fat || 0), 0),
  };

  // Group entries by meal type, preserving order
  const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack', 'snacks'];
  const mealsWithFood = MEAL_ORDER
    .map(mt => {
      const meta = MEAL_META[mt] || { labelKey: null, icon: '🍽️' };
      const label = meta.labelKey ? t(meta.labelKey) : mt;
      const foods = entries.filter(e => (e.meal_type || '').toLowerCase() === mt);
      return {
        meal_type: mt,
        label,
        icon: meta.icon,
        calories: foods.reduce((s, e) => s + (e.calories || 0), 0),
        protein: foods.reduce((s, e) => s + (e.protein || 0), 0),
        carbs: foods.reduce((s, e) => s + (e.carbs || 0), 0),
        fat: foods.reduce((s, e) => s + (e.fat || 0), 0),
        foods,
      };
    })
    // Deduplicate snack/snacks — keep snack if it has food, else fall back to snacks
    .filter((m, idx, arr) => {
      if (m.meal_type === 'snacks') return !arr.find(x => x.meal_type === 'snack' && x.foods.length > 0);
      return m.foods.length > 0;
    });

  const dateLabel = date instanceof Date
    ? date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : (foodLog?.date || '');

  const captureCanvas = async () => {
    if (!slideRef.current) throw new Error('No ref');
    const { default: html2canvas } = await import('html2canvas');
    // canvas captured at full export size (1080×1920 or 1080×1080) with scale:2 → final 2160×3840 / 2160×2160
    return html2canvas(slideRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: theme.bg,
      width: r.w,
      height: r.h,
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      const link = document.createElement('a');
      link.download = `nutrition-${foodLog?.date || 'day'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch { toast.error(t('story.exportFailed')); }
    finally { setExporting(false); }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const canvas = await captureCanvas();
      const imageData = canvas.toDataURL('image/png');
      const postUrl = onBehalfOf
        ? `/social/posts/diet-day?on_behalf_of=${onBehalfOf}`
        : `/social/posts/diet-day`;
      await client.post(postUrl, {
        day_date: foodLog?.date || '',
        day_calories: Math.round(totals.calories),
        day_protein: Math.round(totals.protein),
        day_carbs: Math.round(totals.carbs),
        day_fat: Math.round(totals.fat),
        day_target_calories: targets.calories || 2000,
        day_meals: mealsWithFood.map(m => ({
          meal_type: m.meal_type,
          meal_label: m.label,
          meal_icon: m.icon,
          calories: Math.round(m.calories),
          protein: Math.round(m.protein),
          carbs: Math.round(m.carbs),
          fat: Math.round(m.fat),
          foods: m.foods.map(f => {
            const amt = Math.round((f.serving_size || 100) * (f.servings || f.quantity || 1));
            const unit = f.serving_unit || 'g';
            return {
              name: f.food_name,
              calories: Math.round(f.calories || 0),
              protein: Math.round(f.protein || 0),
              carbs: Math.round(f.carbs || 0),
              fat: Math.round(f.fat || 0),
              amount: amt,
              unit,
            };
          }),
        })),
        day_photo: imageData,
      });
      toast.success(t('story.sharedToFeed'));
      onClose();
    } catch { toast.error(t('story.shareFailed')); }
    finally { setSharing(false); }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] bg-black/90 flex flex-col items-center justify-start overflow-y-auto p-4 pb-8">
      <div className="w-full max-w-xs">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-xl uppercase tracking-wide" style={{ fontFamily: FONT }}>
            {t('story.nutritionDay')}
          </h2>
          <UITooltip>
            <TooltipTrigger asChild>
              <button onClick={onClose} className="text-white/60 hover:text-white p-1">
                <X className="h-6 w-6" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('common.close')}</TooltipContent>
          </UITooltip>
        </div>

        {/* Ratio toggle */}
        <div className="flex gap-2 mb-3">
          {Object.entries(RATIOS).map(([key, val]) => (
            <button
              key={key}
              onClick={() => setRatio(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                ratio === key ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:text-white'
              }`}
            >
              {key === 'story' ? <RectangleVertical className="h-3 w-3" /> : <Square className="h-3 w-3" />}
              {val.label}
            </button>
          ))}
        </div>

        {/* BG toggle */}
        <div className="flex gap-1.5 flex-wrap mb-5">
          {BG_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => setBg(opt.id)}
              className={`px-2.5 py-1 rounded text-xs font-medium border-2 transition-all ${
                bg === opt.id ? 'border-primary' : 'border-transparent'
              }`}
              style={{ background: opt.bg, color: opt.text }}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>

        {/* Canvas preview — slide renders at full export size, CSS-scaled for display */}
        <div className="flex justify-center mb-5">
          <div
            style={{
              width: r.previewW,
              height: r.previewH,
              borderRadius: 12,
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            <div
              style={{
                transform: `scale(${r.previewW / r.w})`,
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <div
                ref={slideRef}
                style={{
                  width: r.w,
                  height: r.h,
                  background: theme.bg,
                }}
              >
                <SlideContent
                  theme={theme}
                  totals={totals}
                  targets={targets}
                  mealsWithFood={mealsWithFood}
                  dateLabel={dateLabel}
                  userName={getDisplayName(user?.name)}
                  previewW={r.w}
                  previewH={r.h}
                  t={t}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
            onClick={handleExport}
            disabled={exporting || sharing}
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            {t('story.download')}
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            onClick={async () => {
              setExporting(true);
              try {
                const canvas = await captureCanvas();
                await nativeShareImage(canvas, {
                  title: t('storyShare.shareNutritionDayTitle', 'Nutrition Day — trainedbyRS'),
                  text: t('storyShare.shareNutritionDayText', 'Check out my nutrition day on trainedbyRS!'),
                  fileName: 'trainedbyrs-nutrition.png',
                });
              } catch { toast.error(t('story.shareFailed')); }
              finally { setExporting(false); }
            }}
            disabled={exporting || sharing}
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />}
            {t('story.story')}
          </Button>
          <Button
            className="flex-1 bg-primary hover:bg-[#e05200] text-white"
            onClick={handleShare}
            disabled={exporting || sharing}
          >
            {sharing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />}
            {t('common.share')}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
